import React, {useCallback, useContext, useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

import {FormInput} from 'src/components/input';
import {RequiredMark} from 'src/components/required-mark';
import {ActionWithReasonButton} from 'src/components/save-with-reason/action-with-reason-button';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {WidthItem} from 'src/components/width-item';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {CustomValidatedControl, ValidatedControl} from 'src/contexts/validation-context';
import {ValidationContextProvider} from 'src/contexts/validation-context/validation-context-provider';
import {ACCESS_RIGHTS} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {BASIC_VALIDATION_FAILED} from 'src/modules/create-account/constants';

import Button from '@pb/kit-component-button';
import {FormSection} from '@pb/kit-component-form-section';
import {Group, Item} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import {Message} from '@pb/kit-component-message';
import {Textarea} from '@pb/kit-component-textarea-v2';

import {ManageEntitlementsPage} from '../components/manage-entitlements-page';
import {RoleGrants} from '../role-grants/role-grants';
import {useAssignGrantsToRole} from '../role-grants/useAssignGrantsToRole';
import {getCleanedExternalId, getEntitlementsReturnPath, validateExternalId} from '../utils';

import {
    ROLE_DESCRIPTION_CONTROL,
    ROLE_EXTERNAL_ID_CONTROL,
    ROLE_NAME_CONTROL,
} from './constants';
import {useCreateRole} from './useCreateRole';

import type {ActionOptions} from 'src/components/save-with-reason/action-with-reason-button';
import type {AccessRight} from 'src/hooks/access-rights/constants';


const CreateRole = () => {
    const navigate = useNavigate();
    const {state: navigationState} = useLocation();
    const {setErrorMessage, setSuccessMessage} = useContext(MessageContext);
    const {createRoleOpen, createRoleSubmit} = useEventTracking();
    const {
        proceed,
        setIsChunkChanged,
    } = useConfirmationChunk({chunkName: 'create-role', onProceed: () => {
        setSuccessMessage('Role has been successfully created.');
        navigate(getEntitlementsReturnPath(navigationState));
    }});

    const [busy, setBusy] = useState(false);
    const [createdExternalId, setCreatedExternalId] = useState<string>();
    const metadataDisabled = busy || !!createdExternalId;

    const {
        data,
        setData,
        isChanged: isRoleChanged,
        saveChanges: createRole,
        validateChanges: validateRoleFields,
    } = useCreateRole();

    const {
        data: grantIds,
        setData: setGrantIds,
        isChanged: isGrantsChanged,
        saveChanges: assignGrants,
    } = useAssignGrantsToRole();

    useEffect(() => {
        createRoleOpen();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setIsChunkChanged(isRoleChanged || isGrantsChanged);
    }, [isRoleChanged, isGrantsChanged, setIsChunkChanged]);

    const changeExternalId = useCallback((id: string) => {
        setData(prev => ({...prev, id}));
    }, [setData]);

    const changeName = useCallback((name: string) => {
        setData(prev => ({
            ...prev,
            name,
            id: (name && !prev.id)
                ? getCleanedExternalId(name, 'role')
                : prev.id,
        }));
    }, [setData]);

    const changeDescription = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setData(prev => ({...prev, description: event.target.value}));
    }, [setData]);

    const trimDescription = useCallback((event: React.FocusEvent<HTMLTextAreaElement>) => {
        setData(prev => ({...prev, description: event.currentTarget.value.trim()}));
    }, [setData]);

    const backToAllEntitlements = useCallback(() => {
        navigate(-1);
        createRoleSubmit({submit: false, success: true});
    }, [createRoleSubmit, navigate]);

    const createRoleHandler = useCallback(async (options?: ActionOptions) => {
        if (validateRoleFields()) {
            setErrorMessage(BASIC_VALIDATION_FAILED);
            createRoleSubmit({submit: true, success: false});
            return BASIC_VALIDATION_FAILED;
        }

        setBusy(true);
        try {
            const result = createdExternalId ? {externalId: createdExternalId} : await createRole();
            if (typeof result === 'object' && result.externalId) {
                setCreatedExternalId(result.externalId);
                const assignError = await assignGrants({roleId: result.externalId, ...options});
                if (assignError) {
                    setErrorMessage(assignError);
                } else {
                    proceed();
                }
                createRoleSubmit({submit: true, success: !assignError});
                return assignError;
            } else if (result) {
                createRoleSubmit({submit: true, success: false});
                setErrorMessage(result as string);
                return result as string;
            }
        } finally {
            setBusy(false);
        }
    }, [createdExternalId, createRole, assignGrants, proceed, setErrorMessage, validateRoleFields, createRoleSubmit]);

    const addGrant = useCallback((grantId: string) => {
        setGrantIds(prev => [...new Set([...prev, grantId])]);
    }, [setGrantIds]);

    const removeGrant = useCallback((grantId: string) => {
        setGrantIds(prev => prev.filter(id => id !== grantId));
    }, [setGrantIds]);

    return (
        <ServerResponseHandler
            serverError={null}
            accessRight={ACCESS_RIGHTS.MANAGE_ENTITLEMENTS as AccessRight}
            sectionName="Create Role">
            <ManageEntitlementsPage
                title="Create Role"
                buttonsBlock={(
                    <Group>
                        <ActionWithReasonButton action="Create" legacyLabel="Create" busy={busy} onConfirm={createRoleHandler}/>
                        <Button variant="Type.SECONDARY" disabled={busy} onClick={backToAllEntitlements}>
                            Cancel
                        </Button>
                    </Group>
                )}>
                <Item className="group-item pd-l">
                    {createdExternalId ? (
                        <Message color="info">
                            Role information has been saved. Retry assigning grants to complete creation.
                        </Message>
                    ) : null}
                    <FormSection headline="Role Information">
                        <Group direction="v">
                            <CustomValidatedControl name={ROLE_NAME_CONTROL}>
                                {error => (
                                    <FormInput
                                        disabled={metadataDisabled}
                                        value={data.name}
                                        width={300}
                                        maxLength={64}
                                        label={<RequiredMark label="Role Name"/>}
                                        placeholder="Role Name"
                                        onBlur={changeName}
                                        valid={error ? false : undefined}
                                        errorDescription={error}
                                    />
                                )}
                            </CustomValidatedControl>
                            <CustomValidatedControl name={ROLE_EXTERNAL_ID_CONTROL}>
                                {error => (
                                    <FormInput
                                        disabled={metadataDisabled}
                                        value={data.id}
                                        width={300}
                                        maxLength={64}
                                        validation={id => validateExternalId(id, 'role')}
                                        label={<RequiredMark label="Role External ID"/>}
                                        placeholder="Role External ID"
                                        onBlur={changeExternalId}
                                        valid={error ? false : undefined}
                                        errorDescription={error}
                                    />
                                )}
                            </CustomValidatedControl>
                            <ValidatedControl name={ROLE_DESCRIPTION_CONTROL}>
                                {invalid => (
                                    <WidthItem width={610}>
                                        <Label><RequiredMark label="Description"/></Label>
                                        <Textarea
                                            disabled={metadataDisabled}
                                            maxLength={255}
                                            color={invalid ? 'ERROR' : 'DEFAULT'}
                                            value={data.description}
                                            onChange={changeDescription}
                                            onBlur={trimDescription}
                                        />
                                    </WidthItem>
                                )}
                            </ValidatedControl>
                        </Group>
                    </FormSection>
                    <RoleGrants
                        grantsIds={grantIds}
                        onAddGrant={addGrant}
                        onRemoveGrant={removeGrant}
                    />
                </Item>
            </ManageEntitlementsPage>
        </ServerResponseHandler>
    );
};

export const ValidateCreateRole = () => (
    <ValidationContextProvider>
        <CreateRole/>
    </ValidationContextProvider>
);
